import ProductAddToCartButton from "@gaiaProfessionals/Overrides/Components/ProductAddToCartButton";
import { ProductImageStyles } from "@gaiaProfessionals/Overrides/Components/ProductImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { canAddToCart } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormInputWrapper } from "@insite/mobius/FormField";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    state,
    productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    widgetId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    showTextBadges?: boolean;
    extendedStyles?: RelatedProductItemStyles;
    relationshipType?: string;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface RelatedProductItemStyles {
    container?: InjectableCss;
    productImageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productInfoWrapper?: InjectableCss;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productNameText?: TypographyPresentationProps;
    relationshipLabel?: TypographyPresentationProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    addToListLinkButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productPriceStyles?: ProductPriceStyles;
    actionsWrapper?: InjectableCss;
}

export const relatedProductItemStyles: RelatedProductItemStyles = {
    container: {
        css: css`
            display: flex;
            padding: 5px 0;
            border-bottom: 1px solid #e5e5e5;
            width: 100%;
            @media (max-width: 768px) {
                flex-direction: column;
            }
        `,
    },
    productImageWrapper: {
        css: css`
            flex: 0 0 100px;
            margin-right: 1rem;
            @media (max-width: 768px) {
                margin-bottom: 1rem;
                margin-right: 0;
            }
        `,
    },
    productInfoWrapper: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
        `,
    },
    productBrandWrapper: {
        css: css`
            margin-bottom: 0.5rem;
        `,
    },
    productNameText: {
        variant: "h3",
        size: 16,
        weight: "bold",
        css: css`
            margin-bottom: 0.5rem;
        `,
    },
    relationshipLabel: {
        size: 14,
        color: "secondary",
        css: css`
            margin-bottom: 0.5rem;
        `,
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            margin-bottom: 1rem;
        `,
    },
    actionsWrapper: {
        css: css`
            flex: 0 0 200px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            @media (max-width: 768px) {
                align-items: flex-start;
                margin-top: 1rem;
            }
        `,
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            width: 100%;
            margin-bottom: 0.5rem;
            > div {
                width: 100%;
            }
            display: flex;
            flex-direction: row;
        `,
    },
    qtyGridItem: {
        width: 4,
        css: css`
            align-items: center;
            padding-right: 5px;
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                > ${FormInputWrapper} {
                    input {
                        height: 30px !important;
                    }
                }
            `,
        },
    },
    addToCartButtonGridItem: {
        width: 8,
        css: css`
            padding-right: 20px;
            align-items: center;
            padding-right: 5px;
        `,
    },
    addToListLinkButtonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: {
                size: 13,
                css: css`
                    text-align: center;
                `,
            },
        },
    },
};

const RelatedProductItem = ({
    state,
    widgetId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    relationshipType,
}: Props) => {
    const [styles] = useState(() => mergeToNew(relatedProductItemStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        const defaultProductInfo = {
            qtyOrdered: 1,
            unitOfMeasure: product.unitOfMeasures ? product.unitOfMeasures[0]?.unitOfMeasure : "",
            productId: product.id,
        };

        updateProductInfo({
            id: widgetId,
            productId: product.id,
            qtyOrdered: defaultProductInfo.qtyOrdered,
            unitOfMeasure: defaultProductInfo.unitOfMeasure,
        });

        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const canBeAddedToCart = canAddToCart(state, product, productInfo, true, true);

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.container} data-test-selector="relatedProductItem">
                {showImage && (
                    <StyledWrapper {...styles.productImageWrapper}>
                        <ProductImage
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productImageStyles}
                        />
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.productInfoWrapper}>
                    {showBrand && product.brand && (
                        <StyledWrapper {...styles.productBrandWrapper}>
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        </StyledWrapper>
                    )}

                    {showTitle && (
                        <>
                            <ProductDescription
                                product={productContext}
                                onClick={() => trackProductRecommendation(true)}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        </>
                    )}

                    {showPartNumbers && (
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            showCustomerName={false}
                            showManufacturerItem={false}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    )}

                    {showPrice && (
                        <StyledWrapper {...styles.productPriceWrapper}>
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                extendedStyles={styles.productPriceStyles}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>

                {showAddToCart && (
                    <StyledWrapper {...styles.actionsWrapper}>
                        <GridContainer {...styles.addToCartContainer}>
                            {canBeAddedToCart && (
                                <GridItem {...styles.qtyGridItem}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${widgetId}-qty-${product.id}`}
                                        id={`${widgetId}-qty-${product.id}-label`}
                                    >
                                        {translate("QTY")}
                                    </VisuallyHidden>

                                    <ProductQuantityOrdered
                                        labelOverride=""
                                        extendedStyles={styles.productQuantityOrderedStyles}
                                        id={`${widgetId}-qty-${product.id}`}
                                    />
                                </GridItem>
                            )}
                            <GridItem {...styles.addToCartButtonGridItem}>
                                <ProductAddToCartButton
                                    hidePricing={true}
                                    data-test-selector="addToCartBtn"
                                    extendedStyles={styles.productAddToCartButtonStyles}
                                    onAddToCartClick={() => trackProductRecommendation(false)}
                                />
                            </GridItem>
                        </GridContainer>

                        {showAddToList && (
                            <GridContainer>
                                <GridItem {...styles.addToListLinkButtonGridItem}>
                                    <ProductAddToListLink
                                        data-test-selector="addToListLink"
                                        extendedStyles={styles.productAddToListLinkStyles}
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RelatedProductItem);
